import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { 
  UserIcon, 
  ArrowPathIcon, 
  DocumentIcon, 
  ExclamationCircleIcon, 
  InformationCircleIcon,
  PaperAirplaneIcon,
  BookmarkIcon,
  BookmarkSlashIcon,
  TrashIcon,
  PlusIcon,
  ScaleIcon,
  PaperClipIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  PencilIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  Bars3Icon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { useUploadPreferences } from '../../contexts/UploadPreferencesContext';
import { validateFile, getFileTypeAcceptString, getFileTypeDescription } from '../../utils/uploadConfig';
import MarkdownRenderer from '../common/MarkdownRenderer';
import axios from 'axios';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';

const ChatInterface = ({ 
  messages = [], 
  loading = false, 
  activeDocuments = [], 
  currentSession = null,
  sessions = [],
  pinnedMessages = [],
  onSendMessage,
  onCreateSession,
  onSwitchSession,
  onPinMessage,
  onUnpinMessage,
  onDeleteSession,
  onRenameSession,
  onUploadDocument
}) => {
  const { preferences } = useUploadPreferences();
  
  const [newMessage, setNewMessage] = useState('');
  const [showSidebar, setShowSidebar] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });
  const [showPinned, setShowPinned] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [processingCapabilities, setProcessingCapabilities] = useState(null);
  const [showProcessingWarning, setShowProcessingWarning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const messagesEndRef = useRef(null);
  const latestResponseRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const editInputRef = useRef(null);
  const exportMenuRef = useRef(null);

  const promptSuggestions = [
    "Summarize the Labour Act requirements for employment contracts",
    "Find recent Supreme Court decisions on property law", 
    "What are the duties of an executor in Zimbabwe?",
    "Compare contract formation requirements across African jurisdictions",
    "Research grounds for divorce in Zimbabwe",
    "Find Kenyan case law on commercial arbitration",
    "Find constitutional court judgments from 2023",
    "Analyze employment termination procedures"
  ];
  
  // Scroll to the top of the latest AI response so users see it without fatigue scrolling
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      const isNewResponse = lastMessage.type === 'ai' || lastMessage.type === 'assistant' || lastMessage.type === 'zimlii';
      if (isNewResponse && latestResponseRef.current) {
        latestResponseRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchProcessingCapabilities();
  }, []);

  useEffect(() => {
    if (editingSessionId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingSessionId]);

  const fetchProcessingCapabilities = async () => {
    try {
      const response = await axios.get('/api/documents/processing_capabilities/', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      setProcessingCapabilities(response.data);
    } catch (error) {
      console.warn('Could not fetch document processing capabilities:', error);
    }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || loading) return;
    const messageContent = newMessage.trim();
    setNewMessage('');
    if (onSendMessage) {
      await onSendMessage(messageContent, selectedFiles);
      setSelectedFiles([]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setNewMessage(suggestion);
    inputRef.current?.focus();
  };

  const handlePinToggle = (message) => {
    if (message.isPinned) {
      onUnpinMessage && onUnpinMessage(message.id);
    } else {
      onPinMessage && onPinMessage(message.id);
    }
  };

  const handleNewSession = () => {
    if (onCreateSession) {
      onCreateSession();
    }
  };

  const startEditing = (session) => {
    setEditingSessionId(session.id);
    setEditingTitle(session.title || '');
  };

  const saveEditing = () => {
    if (editingSessionId && editingTitle.trim() && onRenameSession) {
      onRenameSession(editingSessionId, editingTitle.trim());
    }
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const cancelEditing = () => {
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const confirmDelete = (sessionId) => {
    setDeleteConfirmId(sessionId);
    setTimeout(() => setDeleteConfirmId(null), 3000);
  };

  const executeDelete = (sessionId) => {
    onDeleteSession && onDeleteSession(sessionId);
    setDeleteConfirmId(null);
  };
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatSessionDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const filteredSessions = useMemo(() => sessions?.filter(session => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (session.title || '').toLowerCase().includes(q) ||
      (session.last_message_preview || '').toLowerCase().includes(q)
    );
  }) || [], [sessions, searchQuery]);

  const getGroupedSessions = useCallback(() => {
    const groups = { today: [], yesterday: [], thisWeek: [], older: [] };
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today - 86400000);
    const weekAgo = new Date(today - 7 * 86400000);

    filteredSessions.forEach(session => {
      const date = new Date(session.updated_at || session.created_at);
      if (date >= today) groups.today.push(session);
      else if (date >= yesterday) groups.yesterday.push(session);
      else if (date >= weekAgo) groups.thisWeek.push(session);
      else groups.older.push(session);
    });
    return groups;
  }, [filteredSessions]);

  const enhanceContentForLegal = (content) => {
    if (typeof content !== 'string') return content;
    return content.replace(
      /\[([^\]]+)\](?!\()/g,
      '<mark class="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 px-1 rounded">[$1]</mark>'
    );
  };

  // ---- Export / Copy / Download helpers ----
  const formatChatAsText = useCallback(() => {
    if (!messages || messages.length === 0) return '';
    const sessionTitle = currentSession?.title || 'Chat Session';
    const lines = [`=== ${sessionTitle} ===\n`];
    messages.forEach((msg) => {
      const time = msg.timestamp ? new Date(msg.timestamp).toLocaleString() : '';
      const role =
        msg.type === 'user' ? 'You' :
        msg.type === 'ai' || msg.type === 'assistant' ? 'AI Legal Assistant' :
        msg.type === 'zimlii' ? 'Legal Database' :
        'System';
      lines.push(`[${time}] ${role}:`);
      lines.push(msg.content || '');
      if (msg.references && msg.references.length > 0) {
        lines.push('References:');
        msg.references.forEach((ref, i) => lines.push(`  [${i + 1}] ${ref}`));
      }
      lines.push('');
    });
    return lines.join('\n');
  }, [messages, currentSession]);

  const generateDocx = useCallback(async () => {
    if (!messages || messages.length === 0) return;
    const sessionTitle = currentSession?.title || 'Chat Session';
    const children = [];

    // Title
    children.push(
      new Paragraph({
        text: sessionTitle,
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 200 },
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `Exported on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, italics: true, size: 20, color: '888888' }),
        ],
        spacing: { after: 300 },
      }),
      new Paragraph({
        border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' } },
        spacing: { after: 300 },
      })
    );

    messages.forEach((msg) => {
      const time = msg.timestamp ? new Date(msg.timestamp).toLocaleString() : '';
      let role, roleColor;
      if (msg.type === 'user') {
        role = 'You';
        roleColor = '2563EB';
      } else if (msg.type === 'ai' || msg.type === 'assistant') {
        role = 'AI Legal Assistant';
        roleColor = 'D97706';
      } else if (msg.type === 'zimlii') {
        role = 'Legal Database';
        roleColor = '2563EB';
      } else {
        role = 'System';
        roleColor = '6B7280';
      }

      // Role heading
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: role, bold: true, size: 24, color: roleColor }),
            new TextRun({ text: `  —  ${time}`, size: 18, color: '999999' }),
          ],
          spacing: { before: 240, after: 80 },
        })
      );

      // Content paragraphs
      const contentLines = (msg.content || '').split('\n');
      contentLines.forEach((line) => {
        const isBold = line.startsWith('**') && line.endsWith('**');
        const cleanLine = line.replace(/\*\*/g, '').replace(/^#+\s*/, '').replace(/^[->]\s*/, '');
        if (cleanLine.trim()) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: cleanLine,
                  size: 22,
                  bold: isBold || line.startsWith('#'),
                  italics: msg.type === 'user',
                }),
              ],
              indent: msg.type === 'user' ? { left: 720 } : undefined,
              spacing: { after: 60 },
            })
          );
        }
      });

      // References
      if (msg.references && msg.references.length > 0) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'References:', bold: true, size: 20, color: '4B5563' })],
            spacing: { before: 120, after: 40 },
          })
        );
        msg.references.forEach((ref, i) => {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: `[${i + 1}] ${ref}`, size: 20, color: '6B7280' })],
              indent: { left: 360 },
              spacing: { after: 20 },
            })
          );
        });
      }

      // Separator
      children.push(
        new Paragraph({
          border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'EEEEEE' } },
          spacing: { after: 120 },
        })
      );
    });

    const doc = new Document({
      sections: [{
        properties: {},
        children,
      }],
    });

    const blob = await Packer.toBlob(doc);
    const fileName = `${(currentSession?.title || 'chat').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.docx`;
    saveAs(blob, fileName);
    setShowExportMenu(false);
  }, [messages, currentSession]);

  const handleCopyChat = useCallback(async () => {
    const text = formatChatAsText();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
    setShowExportMenu(false);
  }, [formatChatAsText]);

  const handleDownload = useCallback((format) => {
    if (format === 'docx') {
      generateDocx();
      return;
    }
    const content = formatChatAsText();
    if (!content) return;
    const fileName = `${(currentSession?.title || 'chat').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.txt`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  }, [formatChatAsText, generateDocx, currentSession]);
  // ---- End export helpers ----
  
  const renderMessage = (message, showPinButton = true) => {
    switch (message.type || message.role) {
      case 'user':
        return (
          <div className="flex items-start mb-4 justify-end">
            <div className="bg-primary-600 text-white rounded-2xl rounded-br-md py-2.5 px-3 sm:py-3 sm:px-4 max-w-[85%] sm:max-w-3xl shadow-sm">
              <div className="text-sm whitespace-pre-wrap break-words">{message.content}</div>
              <div className="flex justify-between items-center mt-2 text-xs text-primary-100">
                <span>{formatTime(message.timestamp)}</span>
              </div>
            </div>
            <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex-shrink-0 ml-2 sm:ml-3">
              <UserIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        );
        
      case 'ai':
      case 'assistant':
        return (
          <div className="flex items-start mb-4">
            <div className="flex items-center justify-center h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900 flex-shrink-0">
              <ScaleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="ml-2 sm:ml-3 min-w-0 flex-1 bg-white dark:bg-gray-800 rounded-2xl rounded-bl-md py-2.5 px-3 sm:py-3 sm:px-4 shadow-sm border border-gray-200 dark:border-gray-700 max-w-full sm:max-w-3xl break-words">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-amber-600 dark:text-amber-400">AI Legal Assistant</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                    Trained on legal data
                  </span>
                </div>
                {showPinButton && (
                  <button
                    onClick={() => handlePinToggle(message)}
                    className="text-gray-400 hover:text-amber-500 transition-colors ml-2"
                    title={message.isPinned ? "Unpin message" : "Pin message"}
                  >
                    {message.isPinned ? <BookmarkSlashIcon className="h-4 w-4" /> : <BookmarkIcon className="h-4 w-4" />}
                  </button>
                )}
              </div>
              <div className="text-sm text-gray-800 dark:text-gray-200">
                <MarkdownRenderer content={enhanceContentForLegal(message.content)} className="ai-message-content" />
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-gray-400 dark:text-gray-500">{formatTime(message.timestamp)}</span>
              </div>
              {message.references && message.references.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">References:</p>
                  <ul className="text-xs space-y-1">
                    {message.references.map((ref, index) => (
                      <li key={`ref-${message.id}-${index}`} className="text-gray-600 dark:text-gray-300">
                        <span className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-1 rounded mr-2">[{index + 1}]</span>
                        {ref}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );
        
      case 'zimlii':
        return (
          <div className="flex items-start mb-4">
            <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex-shrink-0">
              <ScaleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-2 sm:ml-3 min-w-0 flex-1 bg-blue-50 dark:bg-blue-900/20 rounded-2xl rounded-bl-md py-2.5 px-3 sm:py-3 sm:px-4 shadow-sm border border-blue-200 dark:border-blue-700 max-w-full sm:max-w-3xl break-words">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Legal Database</span>
                  <span className="text-xs text-blue-500 dark:text-blue-400 bg-blue-100 dark:bg-blue-800 px-2 py-0.5 rounded-full">
                    {message.jurisdiction || 'Multi-Jurisdiction'}
                  </span>
                </div>
                {showPinButton && (
                  <button
                    onClick={() => handlePinToggle(message)}
                    className="text-gray-400 hover:text-blue-500 transition-colors ml-2"
                    title={message.isPinned ? "Unpin message" : "Pin message"}
                  >
                    {message.isPinned ? <BookmarkSlashIcon className="h-4 w-4" /> : <BookmarkIcon className="h-4 w-4" />}
                  </button>
                )}
              </div>
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <MarkdownRenderer content={enhanceContentForLegal(message.content)} className="zimlii-message-content" />
              </div>
              {message.results && (
                <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-4">
                      <span className="text-blue-600 dark:text-blue-400">{message.results.count || 0} total results</span>
                      {message.searchQuery && (
                        <span className="text-blue-500 dark:text-blue-400">Query: "{message.searchQuery}"</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-blue-400 dark:text-blue-500">{formatTime(message.timestamp)}</span>
              </div>
            </div>
          </div>
        );

      case 'system':
        return (
          <div className="flex justify-center mb-4">
            <div className={`py-1 px-3 rounded-full text-xs inline-flex items-center ${
              message.isError
                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                : message.isSystem || message.type === 'system'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
            }`}>
              {message.isError ? <ExclamationCircleIcon className="h-3 w-3 mr-1" /> : <InformationCircleIcon className="h-3 w-3 mr-1" />}
              {message.content}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const errors = [];
    const warnings = [];
    
    files.forEach(file => {
      const validation = validateFile(file, preferences);
      if (validation.isValid) {
        validFiles.push(file);
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (fileExtension === 'pdf' || ['jpg', 'jpeg', 'png', 'tiff', 'bmp'].includes(fileExtension)) {
          if (!processingCapabilities?.scanned_pdf_support && fileExtension === 'pdf') {
            warnings.push(`${file.name}: May be a scanned PDF. Text extraction could be limited.`);
          } else if (!processingCapabilities?.image_ocr_support && ['jpg', 'jpeg', 'png', 'tiff', 'bmp'].includes(fileExtension)) {
            warnings.push(`${file.name}: Image file selected. OCR capabilities not available.`);
          }
        }
      } else {
        errors.push(`${file.name}: ${validation.errors.join(', ')}`);
      }
    });

    if (errors.length > 0) {
      alert(`Some files could not be selected:\n\n${errors.join('\n')}`);
    }

    if (warnings.length > 0) {
      const warningMessage = `Document Processing Limitations:\n\n${warnings.join('\n')}\n\nText extraction may be limited.`;
      if (window.confirm(`${warningMessage}\n\nDo you want to proceed?`)) {
        setShowProcessingWarning(true);
        setTimeout(() => setShowProcessingWarning(false), 10000);
      } else {
        e.target.value = '';
        return;
      }
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
    e.target.value = '';
  };

  const removeSelectedFile = (indexToRemove) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const groups = getGroupedSessions();

  const renderSessionGroup = (label, sessionList) => {
    if (!sessionList || sessionList.length === 0) return null;
    return (
      <div className="mb-3">
        <div className="px-3 py-1 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
          {label}
        </div>
        {sessionList.map(session => (
          <div
            key={session.id}
            className={`group flex items-center px-3 py-2.5 mx-1 rounded-lg cursor-pointer transition-all duration-150 ${
              currentSession?.id === session.id
                ? 'bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
          >
            {editingSessionId === session.id ? (
              <div className="flex-1 flex items-center space-x-1">
                <input
                  ref={editInputRef}
                  type="text"
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEditing();
                    if (e.key === 'Escape') cancelEditing();
                  }}
                  className="flex-1 text-sm px-2 py-1 border border-primary-300 dark:border-primary-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:outline-none"
                />
                <button onClick={saveEditing} className="p-1 text-green-500 hover:text-green-600" title="Save">
                  <CheckIcon className="h-4 w-4" />
                </button>
                <button onClick={cancelEditing} className="p-1 text-gray-400 hover:text-gray-600" title="Cancel">
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => onSwitchSession && onSwitchSession(session.id)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-center">
                    <ChatBubbleLeftRightIcon className={`h-4 w-4 mr-2 flex-shrink-0 ${
                      currentSession?.id === session.id ? 'text-primary-500' : 'text-gray-400 dark:text-gray-500'
                    }`} />
                    <span className={`text-sm truncate ${
                      currentSession?.id === session.id 
                        ? 'font-medium text-primary-700 dark:text-primary-300' 
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {session.title || 'Untitled Session'}
                    </span>
                  </div>
                  {session.last_message_preview && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5 ml-6">
                      {session.last_message_preview}
                    </p>
                  )}
                </button>
                <div className="flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                  <span className="text-xs text-gray-400 dark:text-gray-500 mr-1 whitespace-nowrap">
                    {formatSessionDate(session.updated_at || session.created_at)}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); startEditing(session); }}
                    className="p-1 text-gray-400 hover:text-primary-500 transition-colors"
                    title="Rename session"
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                  </button>
                  {deleteConfirmId === session.id ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); executeDelete(session.id); }}
                      className="p-1 text-red-500 hover:text-red-600 transition-colors animate-pulse"
                      title="Click again to confirm delete"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); confirmDelete(session.id); }}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete session"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex h-full relative">
      {/* Mobile sidebar backdrop */}
      {showSidebar && (
        <div
          onClick={() => setShowSidebar(false)}
          className="md:hidden fixed inset-0 bg-gray-900/50 z-20"
          aria-hidden="true"
        />
      )}
      {/* Session Sidebar */}
      <div
        className={`${showSidebar ? 'w-72' : 'w-0'} flex-shrink-0 transition-all duration-200 overflow-hidden border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 ${
          showSidebar ? 'absolute inset-y-0 left-0 z-30 shadow-xl md:shadow-none md:relative md:z-auto' : ''
        }`}
      >
        <div className="flex flex-col h-full w-72">
          {/* Sidebar Header */}
          <div className="flex-shrink-0 p-3 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={handleNewSession}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              <span>New Chat</span>
            </button>
          </div>
          
          {/* Session Search */}
          <div className="flex-shrink-0 px-3 py-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sessions..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 focus:outline-none"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Session List */}
          <div className="flex-1 overflow-y-auto py-1">
            {filteredSessions.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <ChatBubbleLeftRightIcon className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  {searchQuery ? 'No matching sessions' : 'No chat sessions yet'}
                </p>
                {!searchQuery && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Start a new chat to begin</p>
                )}
              </div>
            ) : (
              <>
                {renderSessionGroup('Today', groups.today)}
                {renderSessionGroup('Yesterday', groups.yesterday)}
                {renderSessionGroup('This Week', groups.thisWeek)}
                {renderSessionGroup('Older', groups.older)}
              </>
            )}
          </div>

          {/* Sidebar Footer */}
          <div className="flex-shrink-0 p-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
              <span>{sessions?.length || 0} sessions</span>
              {pinnedMessages && pinnedMessages.length > 0 && (
                <button
                  onClick={() => setShowPinned(!showPinned)}
                  className="flex items-center space-x-1 text-amber-500 hover:text-amber-600 transition-colors"
                >
                  <BookmarkIcon className="h-3.5 w-3.5" />
                  <span>{pinnedMessages.length} pinned</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0">
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                className="p-1.5 mr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                title={showSidebar ? 'Hide sidebar' : 'Show sidebar'}
              >
                <Bars3Icon className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {currentSession?.title || 'New Chat'}
                </h3>
                {currentSession && (
                  <div className="flex items-center space-x-2 text-xs text-gray-400 dark:text-gray-500">
                    <ClockIcon className="h-3 w-3" />
                    <span>{formatSessionDate(currentSession.updated_at || currentSession.created_at)}</span>
                    {currentSession.message_count > 0 && (
                      <span>&middot; {currentSession.message_count} messages</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
            {pinnedMessages && pinnedMessages.length > 0 && (
              <button
                onClick={() => setShowPinned(!showPinned)}
                className={`flex items-center space-x-1.5 px-2 sm:px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  showPinned
                    ? 'text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-900/30'
                    : 'text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:text-gray-400 dark:hover:text-amber-400 dark:hover:bg-amber-900/20'
                }`}
              >
                <BookmarkIcon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{pinnedMessages.length} Pinned</span>
                <span className="sm:hidden">{pinnedMessages.length}</span>
              </button>
            )}

            {/* Export / Copy / Download Menu */}
            {messages.length > 0 && (
              <div className="relative" ref={exportMenuRef}>
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="flex items-center space-x-1.5 px-2 sm:px-3 py-1.5 text-xs font-medium rounded-md transition-colors text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:text-gray-400 dark:hover:text-primary-400 dark:hover:bg-primary-900/20"
                  title="Export chat"
                  aria-label="Export chat"
                >
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Export</span>
                  <ChevronDownIcon className={`h-3 w-3 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
                </button>

                {showExportMenu && (
                  <div className="absolute right-0 top-full mt-1 w-52 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1 animate-in fade-in slide-in-from-top-1">
                    <button
                      onClick={handleCopyChat}
                      className="w-full flex items-center px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4 mr-3 text-gray-400" />
                      <div className="text-left">
                        <div className="font-medium">{copySuccess ? 'Copied!' : 'Copy to Clipboard'}</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500">Copy full conversation</div>
                      </div>
                      {copySuccess && <CheckIcon className="h-4 w-4 ml-auto text-green-500" />}
                    </button>
                    <button
                      onClick={() => handleDownload('txt')}
                      className="w-full flex items-center px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <DocumentTextIcon className="h-4 w-4 mr-3 text-gray-400" />
                      <div className="text-left">
                        <div className="font-medium">Download as Text</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500">.txt plain text file</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleDownload('docx')}
                      className="w-full flex items-center px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <DocumentIcon className="h-4 w-4 mr-3 text-blue-500" />
                      <div className="text-left">
                        <div className="font-medium">Download as Word</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500">.docx formatted document</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Pinned Messages Section */}
        {pinnedMessages && pinnedMessages.length > 0 && showPinned && (
          <div className="flex-shrink-0 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-200 dark:border-amber-800/50 p-3">
            <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2 flex items-center">
              <BookmarkIcon className="h-3.5 w-3.5 mr-1.5" />
              Pinned Messages
            </h4>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {pinnedMessages.map((pinnedMsg) => (
                <div key={pinnedMsg.id} className="bg-white dark:bg-gray-800 rounded-md p-2 shadow-sm border border-amber-200/50 dark:border-amber-700/50 text-xs">
                  <div className="text-gray-700 dark:text-gray-300 line-clamp-2">
                    {pinnedMsg.message?.content || pinnedMsg.content}
                  </div>
                  {pinnedMsg.note && (
                    <div className="text-gray-400 mt-0.5 italic">{pinnedMsg.note}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className="bg-primary-100 dark:bg-primary-900/30 rounded-full p-4 mb-4">
                <ScaleIcon className="h-10 w-10 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Legal Research Assistant</h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-lg">
                Ask legal questions, search across African jurisdictions, get case law references, or upload documents for analysis.
              </p>
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 max-w-md">
                <div className="flex items-center text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                  <ScaleIcon className="h-4 w-4 mr-2" />
                  Multi-Jurisdiction Legal Research
                </div>
                <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
                  Search cases, legislation, and legal precedents across 17 African jurisdictions.
                </p>
                <div className="text-xs text-blue-500 dark:text-blue-400 space-y-1">
                  <div>• Use the Jurisdiction Search button for direct legal database searches</div>
                  <div>• Ask legal questions naturally — AI will provide comprehensive answers</div>
                  <div>• Every result shows jurisdiction, source, and date accessed</div>
                </div>
              </div>
              {activeDocuments && activeDocuments.length > 0 && (
                <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                    <DocumentIcon className="h-4 w-4 mr-2" />
                    Active Documents ({activeDocuments.length})
                  </div>
                  <div className="space-y-1">
                    {activeDocuments.slice(0, 3).map((doc, index) => (
                      <div key={doc.id || doc.document_id || `active-doc-${index}`} className="text-xs text-green-600 dark:text-green-400 truncate">
                        {doc.name || doc.title || `Document ${index + 1}`}
                      </div>
                    ))}
                    {activeDocuments.length > 3 && (
                      <div className="text-xs text-green-500 dark:text-green-400">+{activeDocuments.length - 3} more documents</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {messages.map((message, index) => {
                const isLatestResponse =
                  index === messages.length - 1 &&
                  (message.type === 'ai' || message.type === 'assistant' || message.type === 'zimlii');
                return (
                  <div key={message.id} ref={isLatestResponse ? latestResponseRef : undefined}>
                    {renderMessage(message)}
                  </div>
                );
              })}
              {loading && (
                <div className="flex items-start mb-4">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900 flex-shrink-0">
                    <ArrowPathIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 animate-spin" />
                  </div>
                  <div className="ml-3 bg-white dark:bg-gray-800 rounded-2xl rounded-bl-md py-3 px-4 shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-2">
                      <div className="h-2 w-2 bg-amber-400 rounded-full animate-bounce"></div>
                      <div className="h-2 w-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="h-2 w-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Section */}
        <div className="flex-shrink-0 p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          {activeDocuments && activeDocuments.length > 0 && (
            <div className="mb-3 p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center text-xs font-medium text-blue-800 dark:text-blue-300 mb-1.5">
                <DocumentIcon className="h-3.5 w-3.5 mr-1.5" />
                Active Documents ({activeDocuments.length})
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                {activeDocuments.slice(0, 6).map((doc, index) => (
                  <div key={doc.id || doc.document_id || `grid-doc-${index}`} className="text-xs text-blue-600 dark:text-blue-400 truncate">
                    {doc.name || doc.title || `Document ${index + 1}`}
                  </div>
                ))}
                {activeDocuments.length > 6 && (
                  <div className="text-xs text-blue-500 dark:text-blue-400">+{activeDocuments.length - 6} more</div>
                )}
              </div>
            </div>
          )}

          {messages.length === 0 && (
            <div className="mb-3">
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Try asking:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {promptSuggestions.slice(0, 4).map((suggestion, index) => (
                  <button
                    key={`suggestion-${index}-${suggestion.substring(0, 15)}`}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="text-left px-3 py-2 text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {showProcessingWarning && (
            <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-start">
                <ExclamationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h5 className="text-sm font-medium text-amber-800 dark:text-amber-200">Scanned Document Processing</h5>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    Some selected files may be scanned documents. Text extraction capabilities are limited.
                    {processingCapabilities?.recommendations?.length > 0 && (
                      <span className="block mt-1"><strong>To improve processing:</strong> {processingCapabilities.recommendations[0]}</span>
                    )}
                  </p>
                </div>
                <button onClick={() => setShowProcessingWarning(false)} className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200">
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {selectedFiles.length === 0 && (
            <div className="mb-2 p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
              <div className="flex items-center text-xs text-blue-700 dark:text-blue-300">
                <PaperClipIcon className="h-3 w-3 mr-1" />
                <span>{getFileTypeDescription(preferences)}</span>
              </div>
              {processingCapabilities && !processingCapabilities.ocr_available && (
                <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  Limited scanned document support - text extraction may be incomplete
                </div>
              )}
            </div>
          )}
          
          <div className="relative">
            {selectedFiles.length > 0 && (
              <div className="mb-2 p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  <PaperClipIcon className="h-3.5 w-3.5 mr-1.5" />
                  Attached Files ({selectedFiles.length})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selectedFiles.map((file, index) => (
                    <div key={`selected-file-${file.name}-${file.size}-${index}`} className="flex items-center bg-white dark:bg-gray-700 rounded px-2 py-0.5 text-xs border border-gray-200 dark:border-gray-600">
                      <span className="text-gray-700 dark:text-gray-300 truncate max-w-32" title={file.name}>{file.name}</span>
                      <button onClick={() => removeSelectedFile(index)} className="ml-1.5 text-gray-400 hover:text-red-500 transition-colors" title="Remove file">
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex items-end space-x-2">
              <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} accept={getFileTypeAcceptString(preferences)} className="hidden" />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="p-2 text-gray-400 hover:text-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={`Attach files - ${getFileTypeDescription(preferences)}`}
              >
                <PaperClipIcon className="h-5 w-5" />
              </button>
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask a legal question..."
                  className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  rows="2"
                  disabled={loading}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && selectedFiles.length === 0) || loading}
                  className="absolute bottom-3 right-3 p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
